import { buildAnalysisEmailPayload } from "@/lib/buildAnalysisEmailPayload";
import { loadLeadEmail, saveLeadEmail } from "@/lib/persistence";
import { SENTINEL_API_BASE } from "@/lib/sentinelApiBase";

const API_BASE = SENTINEL_API_BASE;

export const CONSENT_VERSION = "2026-06-01";

export class EmailApiError extends Error {
  constructor(message, { code, status, detail } = {}) {
    super(message);
    this.name = "EmailApiError";
    this.code = code || "unknown";
    this.status = status || 0;
    this.detail = detail || null;
  }
}

async function postJson(path, body) {
  let response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch (err) {
    throw new EmailApiError("Network error", {
      code: "network",
      detail: err.message,
    });
  }

  let data = {};
  try {
    data = await response.json();
  } catch (e) {
    data = {};
  }

  if (!response.ok) {
    throw new EmailApiError(data.message || data.error || `HTTP ${response.status}`, {
      code: data.code || "request_failed",
      status: response.status,
      detail: data,
    });
  }

  return data;
}

export function getLeadSource() {
  if (typeof window === "undefined") return "landing-home";
  return window.location.pathname.includes("/result") ? "landing-result" : "landing-home";
}

export async function subscribeLead({
  email,
  locale,
  wantsApp,
  source,
  analysis,
  consentVersion = CONSENT_VERSION,
  unsubscribeToken,
  sendFirstRecommendationNow,
}) {
  return postJson("/v1/leads", {
    email,
    locale,
    wantsApp,
    source,
    consentVersion,
    unsubscribeToken,
    sendFirstRecommendationNow:
      sendFirstRecommendationNow !== undefined
        ? sendFirstRecommendationNow
        : Boolean(analysis),
    analysis,
  });
}

export async function sendFirstRecommendation({
  email,
  locale,
  analysis,
  unsubscribeToken,
}) {
  return postJson("/v1/leads/first-recommendation", {
    email,
    locale,
    analysis,
    unsubscribeToken,
  });
}

export async function linkAnalysisToLead({
  email,
  locale,
  leadId,
  analysisSessionId,
  analysis,
  unsubscribeToken,
}) {
  return postJson("/v1/analyses/link-to-lead", {
    email,
    locale,
    leadId,
    analysisSessionId,
    analysis,
    unsubscribeToken,
  });
}

export async function unsubscribeLead({ email, locale, token, resubscribeUrl }) {
  return postJson("/v1/leads/unsubscribe", {
    email,
    locale,
    token,
    resubscribeUrl,
  });
}

export async function subscribeNewsletter({ email, locale, confirmUrl }) {
  return postJson("/v1/landing/newsletter/subscribe", {
    email,
    locale,
    confirmUrl,
  });
}

export async function notifyAnalysisSaved({ email, locale, analysis, reportUrl }) {
  return postJson("/v1/analyses/account-saved", {
    email,
    locale,
    analysis,
    reportUrl,
  });
}

export async function submitLeadCapture({ email, wantsApp, locale, result }) {
  const prior = loadLeadEmail();
  const analysis = result ? buildAnalysisEmailPayload(result, locale) : undefined;
  const source = getLeadSource();

  const data = await subscribeLead({
    email,
    locale,
    wantsApp,
    source,
    analysis,
    unsubscribeToken: prior?.unsubscribeToken,
  });

  saveLeadEmail(email, {
    wantsApp,
    unsubscribeToken: data.unsubscribeToken,
    leadId: data.leadId,
    source,
    analysisLinked: Boolean(analysis),
    emailsSent: data.emailsSent,
  });

  return data;
}

export async function linkAnalysisIfNeeded(result, locale) {
  const lead = loadLeadEmail();
  if (!lead?.email || lead.analysisLinked || !result) return null;

  const analysis = buildAnalysisEmailPayload(result, locale);
  if (!analysis) return null;

  const data = await linkAnalysisToLead({
    email: lead.email,
    locale,
    leadId: lead.leadId,
    analysis,
    unsubscribeToken: lead.unsubscribeToken,
  });

  saveLeadEmail(lead.email, {
    wantsApp: lead.wantsApp,
    unsubscribeToken: data.unsubscribeToken || lead.unsubscribeToken,
    leadId: data.leadId || lead.leadId,
    source: lead.source,
    analysisLinked: true,
    emailsSent: data.emailsSent,
  });

  return data;
}
